import ProductBanner from '../components/ProductBanner';import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FiFilter, FiGrid, FiList, FiSearch } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import { getProducts } from '../api';

const CatalogContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: #f8f8f8;
`;

const HeroSection = styled.section`
  height: 30vh;
  background: var(--dark-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;

  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;

    span {
      background: linear-gradient(135deg, #d395ff, #ff7fd9);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
  }
`;

const CatalogContent = styled.div`
  max-width: 1400px;
  margin: -50px auto 0;
  padding: 0 5%;
  position: relative;
  z-index: 10;
`;

const FilterBar = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
  margin-bottom: 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;
  position: relative;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-gray);
  }

  input {
    width: 100%;
    padding: 0.8rem 1rem 0.8rem 3rem;
    border: 1px solid var(--border-light);
    border-radius: 8px;
    font-size: 1rem;
    transition: var(--transition);

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(211,149,255,0.1);
    }
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const FilterSelect = styled.select`
  padding: 0.8rem 2rem 0.8rem 1rem;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1.2rem;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  padding: 0.3rem;

  button {
    padding: 0.5rem 1rem;
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-gray);
    transition: var(--transition);

    &.active {
      background: var(--primary-color);
      color: white;
    }

    &:hover:not(.active) {
      background: #f0f0f0;
    }
  }
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const CategoryTag = styled(motion.button)`
  padding: 0.6rem 1.5rem;
  border: 1px solid var(--border-light);
  border-radius: 30px;
  background: white;
  color: var(--text-gray);
  font-size: 0.95rem;
  cursor: pointer;
  transition: var(--transition);

  &:hover, &.active {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.viewMode === 'grid' ? 3 : 1}, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(${props => props.viewMode === 'grid' ? 2 : 1}, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadMoreButton = styled(motion.button)`
  display: block;
  margin: 3rem auto 0;
  padding: 1rem 3rem;
  background: transparent;
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const NoResults = styled(motion.div)`
  text-align: center;
  padding: 4rem;
  background: white;
  border-radius: 16px;
  box-shadow: var(--shadow-sm);

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 1rem;
  }

  p {
    color: var(--text-gray);
  }
`;

const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortOption, setSortOption] = useState('name_asc');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, selectedCategory, sortOption, searchQuery]);

  useEffect(() => {
    setDisplayedProducts(filteredProducts.slice(0, itemsPerPage));
  }, [filteredProducts, itemsPerPage]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await getProducts();
      setProducts(data);
      
      const uniqueCategories = ['all', ...new Set(data.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];
    
    // Apply search
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'price_asc':
          return a.price - b.price;
        case 'price_desc':
          return b.price - a.price;
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });
    
    setFilteredProducts(filtered);
  };

  const loadMore = () => {
    setItemsPerPage(prev => prev + 9);
  };

  return (
    <CatalogContainer>
      <HeroSection>
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>
            Explore Our <span>Candy Collection</span>
          </h1>
          <p>Discover handcrafted treats made with love</p>
        </HeroContent>
      </HeroSection>

<ProductBanner products={products} />

      <CatalogContent>
        <FilterBar
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <SearchContainer>
            <FiSearch />
            <input
              type="text"
              placeholder="Search candies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>

          <FilterGroup>
            <FilterSelect
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="name_asc">Name: A to Z</option>
              <option value="name_desc">Name: Z to A</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="newest">Newest First</option>
            </FilterSelect>

            <ViewToggle>
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
              >
                <FiGrid /> Grid
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              >
                <FiList /> List
              </button>
            </ViewToggle>
          </FilterGroup>
        </FilterBar>

        <CategoryFilter>
          {categories.map(category => (
            <CategoryTag
              key={category}
              className={selectedCategory === category ? 'active' : ''}
              onClick={() => setSelectedCategory(category)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </CategoryTag>
          ))}
        </CategoryFilter>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            Loading delicious candies...
          </div>
        ) : (
          <>
            <AnimatePresence mode="wait">
              {displayedProducts.length > 0 ? (
                <ProductsGrid
                  viewMode={viewMode}
                  as={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {displayedProducts.map((product, index) => (
                    <ProductCard 
                      key={product.id} 
                      product={product} 
                      index={index}
                      viewMode={viewMode}
                    />
                  ))}
                </ProductsGrid>
              ) : (
                <NoResults
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <h3>No candies found</h3>
                  <p>Try adjusting your filters or search query</p>
                </NoResults>
              )}
            </AnimatePresence>

            {displayedProducts.length < filteredProducts.length && (
              <LoadMoreButton
                onClick={loadMore}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Load More
              </LoadMoreButton>
            )}
          </>
        )}
      </CatalogContent>
    </CatalogContainer>
  );
};

export default Catalog;